import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useState backed by localStorage. Storage can throw (Safari private mode,
 * embedded third-party iframes with cookies blocked), so every access is
 * guarded and simply falls back to in-memory state.
 */
export function usePersistentState<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((previous: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored != null ? (JSON.parse(stored) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  }, [state]);

  const update = useCallback(
    (value: T | ((previous: T) => T)) => {
      const next =
        typeof value === "function"
          ? (value as (previous: T) => T)(latest.current)
          : value;
      setState(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep the value in memory only */
      }
    },
    [key]
  );

  return [state, update];
}
